import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Eye, EyeOff } from 'lucide-react';
import { userService } from '../../../_services';

function ChangePasswordDialog({ open, onOpenChange }) {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState('');
    const [userName, setUserName] = useState('');

    useEffect(() => {
        const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
        const [id, name] = Usermeta_Data.split(':');
        setUserId(id);
        setUserName(name);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error for this field
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        const newErrors = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        };

        if (formData.currentPassword.length < 6) {
            newErrors.currentPassword = 'At least 6 characters in length';
        }

        if (formData.newPassword.length < 6) {
            newErrors.newPassword = 'At least 6 characters in length';
        }

        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Password do not match';
        }

        // Check if there are any errors
        if (newErrors.currentPassword || newErrors.newPassword || newErrors.confirmPassword) {
            setErrors(newErrors);
            return;
        }

        // Reset password
        setLoading(true);
        try {
            const apidata = await userService.ResetPassword(
                userId,
                userName,
                formData.currentPassword,
                formData.newPassword,
                formData.confirmPassword
            );

            if (apidata && apidata.length > 0) {
                const result = apidata[0];
                
                if (result.code === "1") {
                    // Success - close dialog after short delay
                    setTimeout(() => {
                        onOpenChange(false);
                        // Reset form
                        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
                        setLoading(false);
                    }, 600);
                } else if (result.code === "-1") {
                    // Error with current password
                    setErrors(prev => ({ ...prev, currentPassword: result.message }));
                    setLoading(false);
                } else if (result.code === "-2") {
                    // Error with new password
                    setErrors(prev => ({ ...prev, newPassword: result.message }));
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Error resetting password:', error);
            setErrors(prev => ({ ...prev, currentPassword: 'An error occurred. Please try again.' }));
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswords({ current: false, new: false, confirm: false });
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="currentPassword">Current Password</Label>
                            <div className="relative">
                                <Input
                                    id="currentPassword"
                                    name="currentPassword"
                                    type={showPasswords.current ? 'text' : 'password'}
                                    placeholder=" "
                                    value={formData.currentPassword}
                                    onChange={handleInputChange}
                                    onFocus={handleInputChange}
                                    maxLength={30}
                                    className={errors.currentPassword ? 'border-red-500' : ''}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('current')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.currentPassword && (
                                <p className="text-sm text-red-500">{errors.currentPassword}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <div className="relative">
                                <Input
                                    id="newPassword"
                                    name="newPassword"
                                    type={showPasswords.new ? 'text' : 'password'}
                                    placeholder=" "
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    onFocus={handleInputChange}
                                    maxLength={30}
                                    className={errors.newPassword ? 'border-red-500' : ''}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('new')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.newPassword && (
                                <p className="text-sm text-red-500">{errors.newPassword}</p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm Password</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showPasswords.confirm ? 'text' : 'password'}
                                    placeholder=" "
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    onFocus={handleInputChange}
                                    maxLength={30}
                                    className={errors.confirmPassword ? 'border-red-500' : ''}
                                />
                                <button
                                    type="button"
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                >
                                    {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            type="button" 
                            variant="default" 
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </Button>
                        <Button 
                            type="submit"
                            variant="secondary"
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePasswordDialog;
